import React, { useState, useEffect, useRef, useMemo } from "react";
import { useGetCommissionInvoicesQuery, useGetCommissionPnlQuery } from "../endpoints/commissions.endpoints";
import type { GrossMarginRow } from "../types/commissions.types";
import GrossMarginEditor from "../components/gross-margin-editor";

/** Parse paid date string to month (1–12) and year; returns null if missing/invalid */
function parsePaidDate(paidDate: string | undefined): { month: number; year: number } | null {
  if (!paidDate) return null;
  const d = new Date(paidDate);
  if (Number.isNaN(d.getTime())) return null;
  return { month: d.getMonth() + 1, year: d.getFullYear() };
}

const GrossMarginView: React.FC = () => {
  const { data: invoices = [] } = useGetCommissionInvoicesQuery();
  const { data: pnlData = [] } = useGetCommissionPnlQuery();
  const [grossMarginRows, setGrossMarginRows] = useState<GrossMarginRow[]>([]);
  const prevPnlDataRef = useRef(pnlData);

  useEffect(() => {
    if (prevPnlDataRef.current !== pnlData) {
      prevPnlDataRef.current = pnlData;
      if (pnlData.length > 0) setGrossMarginRows(pnlData);
    } else if (pnlData.length > 0 && grossMarginRows.length === 0) {
      setGrossMarginRows(pnlData);
    }
  }, [pnlData, grossMarginRows.length]);

  const getTotalIncomeForMonth = useMemo(() => {
    return (month: number, year: number) => {
      return invoices
        .filter((inv) => {
          if (inv.status !== "paid" || inv.includeInTotal === false) return false;
          const paid = parsePaidDate(inv.paidDate);
          return paid !== null && paid.month === month && paid.year === year;
        })
        .reduce((sum, inv) => sum + inv.amount, 0);
    };
  }, [invoices]);

  return (
    <div className="mt-3 space-y-4">
      <GrossMarginEditor
        rows={grossMarginRows}
        getTotalIncomeForMonth={getTotalIncomeForMonth}
      />
    </div>
  );
};

export default GrossMarginView;
